import {
  ConfirmationModal,
  Flex,
  Heading,
  Modal,
  Text,
  Toast,
  useOpenModal,
} from '@modusign/design-system';
import {
  acceptInvitation as acceptInvitationApi,
  getGetWorkspaceQueryKey,
  getWorkspace,
  useGetUserInvitation,
} from '@modusign/openapi-client-user';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import noop from '@libs/noop';

import CautionImage from '@app/assets/modal/workspace-invitation-accept-modal/caution.svg';
import InfoImage from '@app/assets/modal/workspace-invitation-accept-modal/info.svg';
import isTrialPlan from '@app/features/subscription/libs/isTrialPlan';
import { getSubscriptionPlanId } from '@app/features/subscription/state';
import InvitationCaution from '@app/features/workspaceManagement/workspaceMemberInvitation/components/InvitationAcceptanceModal/InvitationCaution';
import InvitationContent from '@app/features/workspaceManagement/workspaceMemberInvitation/components/InvitationAcceptanceModal/InvitationContent';
import InvitationFooter from '@app/features/workspaceManagement/workspaceMemberInvitation/components/InvitationAcceptanceModal/InvitationFooter';
import InvitationHeader from '@app/features/workspaceManagement/workspaceMemberInvitation/components/InvitationAcceptanceModal/InvitationHeader';
import InvitationInfo from '@app/features/workspaceManagement/workspaceMemberInvitation/components/InvitationAcceptanceModal/InvitationInfo';
import useEnsureQuery from '@app/hooks/useEnsureQuery';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import workspaceService from '@app/libs/workspaceService';
import { stopSubscriptionAPI } from '@app/store/transport-layer/subscription/subscription/stop';

interface InvitationAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InvitationAcceptanceModal({
  isOpen,
  onClose,
}: InvitationAcceptanceModalProps) {
  const history = useHistory();
  const currentWorkspaceId = useWorkspaceId();
  const userInvitation = useGetUserInvitation();
  const subscriptionPlanId = useSelector(getSubscriptionPlanId);
  const isMobileViewport = useResponsiveValue([true, false, false]);
  const openModal = useOpenModal();
  const ensureQuery = useEnsureQuery();
  const { createErrorHandler } = useOpenErrorModal();

  const [invitationStep, setInvitationStep] = useState<'INFO' | 'CAUTION'>('INFO');
  const [isCheckedScheduledDocumentsLabel, setScheduledDocumentsLabel] = useState(false);

  const isSubscriptionActive = !!subscriptionPlanId && !isTrialPlan(subscriptionPlanId);

  const acceptInvitation = useMutation<
    Awaited<ReturnType<typeof acceptInvitationApi>>,
    Error,
    { workspaceId: string; invitationId: string }
  >({
    mutationFn: async ({ workspaceId, invitationId }) => {
      await acceptInvitationApi(workspaceId, invitationId);
      await ensureQuery({
        queryKey: getGetWorkspaceQueryKey(workspaceId),
        queryFn: () => getWorkspace(workspaceId),
      });
    },
    onSuccess: (_, { workspaceId }) => {
      onClose();
      workspaceService.setWorkspaceId(workspaceId);
      history.push('/');
      Toast.success({ description: `${workspaceName} 가입이 완료되었습니다.` });
    },
    onError: (error) => {
      const hasActiveBulkRequest = error.message === 'Active bulk requests exist';
      const hasActiveContractLink = error.message === 'Active contract links exist';

      if (hasActiveBulkRequest) {
        return openModal(({ isOpen, onClose }) => (
          <ConfirmationModal
            confirmButtonLabel="중단하러 가기"
            isOpen={isOpen}
            maxWidth="xxs"
            title="초대 수락 안내"
            variant="warning"
            onClose={onClose}
            onConfirm={() => history.push('/bulk-requests')}
          >
            <Text textAlign="center">진행 중인 대량전송이 있습니다.</Text>
            <Text textAlign="center">중단 후 다시 진행해 주세요.</Text>
          </ConfirmationModal>
        ));
      }
      if (hasActiveContractLink) {
        return openModal(({ isOpen, onClose }) => (
          <ConfirmationModal
            confirmButtonLabel="비활성화하러 가기"
            isOpen={isOpen}
            maxWidth="xxs"
            title="초대 수락 안내"
            variant="warning"
            onClose={onClose}
            onConfirm={() => history.push('/links')}
          >
            <Text textAlign="center">활성화한 링크서명이 있습니다.</Text>
            <Text textAlign="center">비활성화 후 다시 진행해 주세요.</Text>
          </ConfirmationModal>
        ));
      }
      return createErrorHandler({ title: '초대 수락 실패' })(error);
    },
  });

  const stopSubscription = useMutation<
    Awaited<ReturnType<typeof stopSubscriptionAPI>>,
    Error,
    Parameters<typeof stopSubscriptionAPI>
  >({
    mutationFn: (vs) => stopSubscriptionAPI(...vs),
    onSuccess: () =>
      acceptInvitation.mutateAsync({
        workspaceId: invitation.workspaceId,
        invitationId: invitation.id,
      }),
    onError: createErrorHandler({ title: '초대 수락 실패' }),
  });

  const handleAcceptInvitation = () => {
    if (isSubscriptionActive) {
      return openModal(({ isOpen, onClose }) => (
        <ConfirmationModal
          // tagContactButton 클래스로 채널톡을 엽니다.
          confirmButtonClassName="tagContactButton"
          confirmButtonLabel="상담원 연결"
          isOpen={isOpen}
          maxWidth="xxs"
          title="초대 수락 안내"
          variant="warning"
          onClose={onClose}
          onConfirm={noop}
        >
          <Text textAlign="center">이용 중인 요금제가 있습니다.</Text>
          <Text textAlign="center">상담원 연결을 통해 문의 후 다시 진행해 주세요.</Text>
        </ConfirmationModal>
      ));
    }
    if (isTrialPlan(subscriptionPlanId)) {
      return stopSubscription.mutate([currentWorkspaceId]);
    }
    return acceptInvitation.mutate({
      workspaceId: invitation.workspaceId,
      invitationId: invitation.id,
    });
  };

  if (!userInvitation.data) {
    return null;
  }

  const invitation = userInvitation.data;

  const { workspaceName } = invitation;

  return (
    <Modal
      isOpen={isOpen}
      maxWidth={isMobileViewport ? 'xxs' : 'sm'}
      px={9}
      py={9}
      onClose={onClose}
      onRequestClose={onClose}
    >
      <Flex flexDirection="column" width="100%">
        <InvitationHeader currentStep={invitationStep} onClose={onClose} />
        {invitationStep === 'INFO' ? (
          <>
            <InvitationContent
              content={
                <InvitationInfo
                  handleClickCheckBox={() => setScheduledDocumentsLabel((v) => !v)}
                  isChecked={isCheckedScheduledDocumentsLabel}
                />
              }
              image={InfoImage}
              title={
                <Heading fontSize="xl" fontWeight="bold">
                  <Heading
                    as="span"
                    color="textEmphasisBase"
                    display="inline"
                    fontSize="lg"
                    fontWeight="bold"
                  >
                    {workspaceName}
                  </Heading>
                  에서 초대장이 도착했어요!
                </Heading>
              }
            />
            <InvitationFooter
              closeButtonLabel="취소"
              confirmButtonDisabled={!isCheckedScheduledDocumentsLabel}
              confirmButtonLabel="다음"
              onClose={onClose}
              onConfirm={() => setInvitationStep('CAUTION')}
            />
          </>
        ) : (
          <>
            <InvitationContent
              content={<InvitationCaution />}
              image={CautionImage}
              title={
                <Text fontSize="22px" fontWeight="bold">
                  개인 정보 보안을 유의해주세요.
                </Text>
              }
            />
            <InvitationFooter
              closeButtonLabel="이전으로"
              confirmButtonLabel="초대 수락"
              isConfirmButtonLoading={acceptInvitation.isLoading || stopSubscription.isLoading}
              onClose={() => setInvitationStep('INFO')}
              onConfirm={handleAcceptInvitation}
            />
          </>
        )}
      </Flex>
    </Modal>
  );
}
